/**
 * Top-level renderer for a Jubiel Template Package.
 *
 * Takes a package plus a fully-resolved host context and produces the page.
 * It owns nothing but layout: every rule, every fetch and every interactive
 * widget arrives from the host.
 */

import { Fragment, useMemo, type CSSProperties, type ReactNode } from "react";
import { belowPreviewBanner } from "@/lib/previewBannerOffset";
import { evaluateWhen } from "./expression";
import { buildPredicates } from "./context";
import { renderChildren, renderContent, type RenderEnv } from "./render";
import { applyColorScheme, createTokenResolver } from "./tokens";
import type { EngineContext, SectionType, SlotNode, TemplatePackage, TemplateSection } from "./types";

/** Default anchors per section type — kept identical across templates so guest deep links survive a redesign. */
export const SECTION_ANCHORS: Record<SectionType, string | null> = {
  navbar: null,
  hero: "home",
  cover: "cover",
  countdown: "countdown",
  story: "story",
  gallery: "gallery",
  family: "family",
  details: "details",
  rsvp: "rsvp",
  saveTheDate: "save-the-date",
  registry: "gifts",
  footer: null,
  custom: null,
};

/** Section types that earn a navigation entry, and the key that labels them. */
export const SECTION_NAV_KEYS: Partial<Record<SectionType, string>> = {
  hero: "tpl.nav.home",
  story: "tpl.nav.story",
  gallery: "tpl.nav.gallery",
  details: "tpl.nav.details",
  rsvp: "tpl.nav.rsvp",
  registry: "tpl.nav.registry",
};

/**
 * Layer for a `fixed-top` section.
 *
 * Deliberately below the host dialog layer (shadcn/Radix portals to `document.body`
 * at z-50) and above page content, so a floating navbar cannot cover the RSVP or
 * gift dialog it just opened.
 */
const FIXED_SECTION_Z = 40;

export type TemplateRendererProps = {
  pkg: TemplatePackage;
  ctx: EngineContext;
  slots?: Partial<Record<SlotNode["name"], ReactNode>>;
};

/**
 * A section's padding splits: the vertical part belongs to the section band, the
 * horizontal part to the centred content box. That is the `max-w-5xl mx-auto px-6`
 * idiom — the gutter eats into the measure rather than sitting outside it, so a
 * capped column is narrower than its cap by exactly the gutter.
 */
function splitPadding(padding: string | undefined): { block?: string; inline?: string } {
  if (!padding) return {};
  const parts = padding.split(" ");
  return parts.length === 1 ? { block: parts[0], inline: parts[0] } : { block: parts[0], inline: parts[1] };
}

function sectionStyle(section: TemplateSection, env: RenderEnv): CSSProperties {
  const { block } = splitPadding(section.padding);
  const base: CSSProperties = {
    background: env.tokens.background(section.background),
    paddingTop: env.tokens.space(block),
    paddingBottom: env.tokens.space(block),
    minHeight: section.minHeight === "screen" ? "100vh" : section.minHeight,
    overflow: section.overflow,
    position: "relative",
  };

  if (section.position === "fixed-top") {
    return {
      ...base,
      paddingTop: undefined,
      paddingBottom: undefined,
      padding: env.tokens.padding(section.padding),
      position: "fixed",
      top: belowPreviewBanner(env.tokens.space(section.offset) ?? "1rem"),
      left: "50%",
      transform: "translateX(-50%)",
      width: "92%",
      maxWidth: env.tokens.maxWidth(section.maxWidth) ?? "64rem",
      zIndex: FIXED_SECTION_Z,
    };
  }
  return base;
}

export function TemplateRenderer({ pkg, ctx, slots = {} }: TemplateRendererProps) {
  const tokens = useMemo(
    () => createTokenResolver(applyColorScheme(pkg.tokens, ctx.event.colorScheme), {
      fontScale: ctx.fontScale,
      prefersReducedMotion: ctx.prefersReducedMotion,
      fonts: pkg.manifest.fonts,
    }),
    [pkg.tokens, pkg.manifest.fonts, ctx.event.colorScheme, ctx.fontScale, ctx.prefersReducedMotion],
  );

  const predicates = useMemo(() => buildPredicates(ctx), [ctx]);
  const declaredSlots = new Set<SlotNode["name"]>();
  const env: RenderEnv = {
    ctx, tokens, predicates, slots, declaredSlots,
    fragments: pkg.layout.fragments ?? {},
  };

  const visibleSections = pkg.sections.filter((section) => evaluateWhen(section.when, predicates));

  // Rendered before the JSX so `declaredSlots` is populated by the time the
  // overlays below are mounted.
  const sectionMarkup = visibleSections.map((section) => {
    const anchor = section.anchor ?? SECTION_ANCHORS[section.type] ?? undefined;
    const { nodes: content, empty } = renderContent(section.children, env, {});
    const decorations = section.decorations ? renderChildren(section.decorations, env, {}) : [];

    // A section whose entire body resolved away renders nothing at all, so a
    // hidden field can never leave an empty band with a live anchor. Same rule as
    // a stack: a body left with only spacers and dividers counts as empty.
    if (empty || content.length === 0) return null;

    const { inline } = splitPadding(section.padding);

    return (
      <section
        key={section.id}
        id={anchor}
        data-section={section.id}
        data-section-type={section.type}
        style={sectionStyle(section, env)}
      >
        {decorations}
        <div
          style={{
            maxWidth: tokens.maxWidth(section.maxWidth),
            marginLeft: section.maxWidth ? "auto" : undefined,
            marginRight: section.maxWidth ? "auto" : undefined,
            paddingLeft: tokens.space(inline),
            paddingRight: tokens.space(inline),
            position: "relative",
            zIndex: 10,
          }}
        >
          {content}
        </div>
      </section>
    );
  });

  return (
    <div
      className={tokens.rootClass}
      data-template={pkg.manifest.key}
      style={{
        background: tokens.background(pkg.layout.page?.background) ?? tokens.color("palette.background"),
        fontFamily: tokens.fontFamily("body"),
        position: "relative",
        overflowX: "hidden",
        minHeight: "100vh",
      }}
    >
      <style>{tokens.css}</style>

      {pkg.layout.page?.decorations && renderChildren(pkg.layout.page.decorations, env, {})}

      <div style={{ position: "relative", zIndex: 10 }}>{sectionMarkup}</div>

      {/* Host overlays, mounted outside every section. Each is a fixed-position
          surface, and one rendered inside a section is trapped by that section's
          stacking context — the RSVP dialog ended up beneath a floating navbar
          that was only a sibling with a higher z-index. The template declares
          which it wants; where they land in the DOM is not its business. */}
      {[...declaredSlots].map((name) => (
        <Fragment key={name}>{slots[name]}</Fragment>
      ))}
    </div>
  );
}

/** Navigation entries a host chrome can render for this page, in section order. */
export function navigationEntries(pkg: TemplatePackage, predicates: Record<string, boolean>) {
  return pkg.sections
    .filter((section) => evaluateWhen(section.when, predicates))
    .filter((section) => (section.nav ?? Boolean(SECTION_NAV_KEYS[section.type])))
    .map((section) => ({
      anchor: section.anchor ?? SECTION_ANCHORS[section.type] ?? section.id,
      labelKey: SECTION_NAV_KEYS[section.type] ?? `tpl.nav.${section.id}`,
    }));
}
